import { cn } from "@universe/ui/lib/utils";
import { Briefcase, User } from "lucide-react";

import { AccountType, LegalEntityType } from "@generated/types";

type AccountOrEntityType = LegalEntityType | AccountType | string;

interface AccountTypeIconProps {
  type: AccountOrEntityType;
  label?: string;
  showLabel?: boolean;
  size?: number;
  className?: string;
  iconClassName?: string;
  labelClassName?: string;
  ariaLabel?: string;
  iconStyle?: React.CSSProperties;
}

const isPersonalType = (type: AccountOrEntityType): boolean => {
  return (
    type === LegalEntityType.Person ||
    type === AccountType.Personal ||
    type === "PERSON" ||
    type === "PERSONAL"
  );
};

const isBusinessType = (type: AccountOrEntityType): boolean => {
  return (
    type === LegalEntityType.Corporation ||
    type === AccountType.Corporate ||
    type === AccountType.SoleTrader ||
    type === AccountType.LiquidityProvider ||
    type === AccountType.Treasury ||
    type === "CORPORATION" ||
    type === "CORPORATE" ||
    type === "SOLE_TRADER" ||
    type === "LIQUIDITY_PROVIDER" ||
    type === "TREASURY"
  );
};

export const AccountTypeIcon = ({
  type,
  label,
  showLabel = true,
  size = 16,
  className,
  iconClassName,
  labelClassName,
  ariaLabel,
  iconStyle,
}: AccountTypeIconProps) => {
  const isPerson = isPersonalType(type);
  const isBusiness = isBusinessType(type);

  if (!isPerson && !isBusiness) {
    // If type doesn't match, render label only if provided
    return showLabel && label ? (
      <span className={cn(labelClassName)}>{label}</span>
    ) : null;
  }

  const Icon = isPerson ? User : Briefcase;

  if (!showLabel || !label) {
    return (
      <Icon
        className={cn(iconClassName)}
        size={size}
        strokeWidth={2}
        style={iconStyle}
        aria-label={ariaLabel || label || (isPerson ? "Personal" : "Business")}
      />
    );
  }

  return (
    <span className={cn("inline-flex items-center gap-1.5", className)}>
      <Icon
        className={cn(iconClassName)}
        size={size}
        strokeWidth={2}
        style={iconStyle}
        aria-hidden="true"
      />
      <span className={cn(labelClassName)}>{label}</span>
    </span>
  );
};

export default AccountTypeIcon;
